import { cn } from "@/lib/utils";
import { TOXICOLOGY_EYEBROWS, TOXICOLOGY_HERO_EYEBROW } from "@/data/qaSectionEyebrows";
import { useState } from "react";
import clinicalExpertImage from "@/assets/services/clinical-solutions/offerings/clinical-expert-hero.jpg";
import { QA_OVERVIEW_CARDS } from "@/data/qaOverview";
import { useParams, useLocation, Navigate } from "@/lib/router-compat";
import { DIVISION_LOGOS } from "@/lib/divisionLogos";
import SEOHead from "@/components/SEOHead";
import { getHubOgImage } from "@/lib/seo/hubOgImages";
import Navbar from "@/components/Navbar";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import CirAccreditationBlock from "@/components/services/CirAccreditationBlock";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getServiceBySlug, getServiceByPath } from "@/data/servicesData";
import ServiceHero from "@/components/services/ServiceHero";
import { heroTakeaway, getKeyTakeaway, keyTakeawaySchema } from "@/data/keyTakeaways";
import { questionHeading, softwareOfferingsHeadings } from "@/data/questionHeadings";
import ServiceValueProps from "@/components/services/ServiceValueProps";
import ServiceChallenges from "@/components/services/ServiceChallenges";
import ServiceOfferings from "@/components/services/ServiceOfferings";
import ServiceIndustryLinks from "@/components/services/ServiceIndustryLinks";
import ServiceWhyQbd from "@/components/services/ServiceWhyQbd";
import ServiceContact from "@/components/services/ServiceContact";
import ServiceRelatedContent from "@/components/services/ServiceRelatedContent";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { getServiceContent } from "@/i18n/serviceDetail";
import { uiT } from "@/i18n/ui";
import { localizeHref } from "@/lib/localizeHref";

const TOX_ACCENT = "var(--qa-copy-toxicology)";

const ServicePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [formOpen, setFormOpen] = useState(false);
  const lang = useLanguage();
  const prefix = langPrefix(lang);
  const ui = uiT(lang);
  const servicesPath = localizeHref("/en/services", lang);

  const service = getServiceByPath(location.pathname) ?? (slug ? getServiceBySlug(slug) : undefined);

  if (!service) return <Navigate to={servicesPath} replace />;
  // Unified eyebrow system: English Regulatory Toxicology page only.
  const toxUnified = lang === "en" && service.slug === "toxicology";

  // Merge translated content over the English defaults
  const i18n = getServiceContent(service.slug, lang);

  const displayService = i18n
    ? {
        ...service,
        title: i18n.title,
        subtitle: i18n.subtitle,
        description: i18n.description,
        valueProps: service.valueProps.map((vp, idx) =>
          i18n.valueProps[idx]
            ? { ...vp, title: i18n.valueProps[idx].title, description: i18n.valueProps[idx].description }
            : vp
        ),
        challengesTitle: i18n.challengesTitle,
        challengesContent: i18n.challengesContent,
        whyQbd: {
          ...service.whyQbd,
          intro: i18n.whyQbd.intro,
          bullets: i18n.whyQbd.bullets,
        },
        formHeading: i18n.formHeading,
        formSubtitle: i18n.formSubtitle,
        industryLinks: i18n.industryLinks && service.industryLinks
          ? service.industryLinks.map((link, idx) =>
              i18n.industryLinks![idx]
                ? { ...link, title: i18n.industryLinks![idx].title, description: i18n.industryLinks![idx].description }
                : link
            )
          : service.industryLinks,
      }
    : service;

  const SITE = "https://qbdgroup.com";
  // The hero intro (subtitle + description) is the visible answer-first block.
  // Fall back to the curated takeaway copy only when the hero has no description.
  const takeaway =
    heroTakeaway(displayService.subtitle, displayService.description, getKeyTakeaway(service.slug, lang));

  // GEO: question-shaped H2s so each section answers a real query.
  const q = (section: Parameters<typeof questionHeading>[0]) =>
    questionHeading(section, displayService.title, lang);
  const swHeadings = softwareOfferingsHeadings(lang);

  return (
    <>
      <SEOHead
        title={`${displayService.title} | QbD Group`}
        description={displayService.description.slice(0, 155)}
        canonical={`${SITE}${location.pathname}`}
        ogImage={getHubOgImage(location.pathname) ?? displayService.heroImage}
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: displayService.title,
            description: displayService.description,
            provider: {
              "@type": "Organization",
              name: "QbD Group",
              url: SITE,
            },
            url: `${SITE}${location.pathname}`,
            ...keyTakeawaySchema(takeaway),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: lang === "es" ? "Inicio" : lang === "zh-cn" ? "首页" : "Home", item: `${SITE}${localizeHref("/", lang)}` },
              { "@type": "ListItem", position: 2, name: lang === "es" ? "Servicios" : lang === "zh-cn" ? "服务" : "Services", item: `${SITE}${servicesPath}` },
              { "@type": "ListItem", position: 3, name: displayService.title },
            ],
          })}
        </script>
      </SEOHead>

      <ScrollToTop />
      <Navbar {...(DIVISION_LOGOS[service.slug] ? { logoOverride: DIVISION_LOGOS[service.slug] } : {})} />
      <CommercialBrowseSidebar />

      <main className={cn("pb-16 sm:pb-0", toxUnified && "qa-unified-headings")} style={{ '--service-color': displayService.color } as React.CSSProperties}>
        <ServiceHero
          service={displayService}
          onCtaClick={() => setFormOpen(true)}
          partnerLogo={undefined}
          eyebrow={toxUnified ? TOXICOLOGY_HERO_EYEBROW : undefined}
          eyebrowAccent={toxUnified ? TOX_ACCENT : undefined}
        />
        
        <ServiceValueProps valueProps={displayService.valueProps} color={displayService.color} />
        <ServiceChallenges
          title={q("challenges")}
          content={displayService.challengesContent}
          image={displayService.challengesImage}
          color={displayService.color}
          eyebrow={toxUnified ? TOXICOLOGY_EYEBROWS.challenges : undefined}
          eyebrowAccent={TOX_ACCENT}
        />
        {displayService.industryLinks ? (
          <ServiceIndustryLinks
            links={displayService.industryLinks}
            groupHeading={
              service.slug === "clinical-solutions"
                ? (lang === "es" ? "Servicios CRO" : lang === "zh-cn" ? "CRO 服务" : "CRO Services")
                : undefined
            }
            extraGroups={
              service.slug === "clinical-solutions"
                ? [
                    {
                      heading:
                        lang === "es"
                          ? "Servicios de expertos y recursos flexibles"
                          : lang === "zh-cn"
                            ? "专家服务与灵活人力资源"
                            : "Expert Services & Flexible Resourcing",
                      links: [
                        {
                          title:
                            lang === "es"
                              ? "Servicios de expertos clínicos y recursos flexibles"
                              : lang === "zh-cn"
                                ? "临床专家服务与灵活人力资源"
                                : "Clinical Expert Services & Flexible Resourcing",
                          description:
                            lang === "es"
                              ? "Incorpora CRAs, CTAs y clinical project managers a tu equipo en toda Europa. Recursos clínicos flexibles sin externalizar todo tu estudio."
                              : lang === "zh-cn"
                                ? "在整个欧洲为您的团队补充 CRA、CTA 和临床项目经理。灵活的临床人力资源，无需外包整个研究。"
                                : "Add CRAs, CTAs and clinical project managers to your team across Europe. Flexible clinical resourcing without outsourcing your full study.",
                          image: clinicalExpertImage,
                          href: "/en/services/clinical-solutions/clinical-expert-services",
                        },
                      ],
                    },
                  ]
                : undefined
            }
            sectionTitle={q("industryLinks")}
            sectionSubtitle={lang === "es" ? "Nuestros servicios" : lang === "zh-cn" ? "我们的服务" : "Our services"}
          />
        ) : service.slug === "software-solutions-services" ? (
          <>
            <ServiceOfferings
              serviceSlug={service.slug}
              color={displayService.color}
              imageFit="contain"
              sectionTitle={swHeadings.solutions}

              onlyIds={[
                "4327024a-3cc4-47d8-a418-7db6eb3aabbf",
                "4125e687-14ad-48ab-8aa7-6e793b7fa6c7",
              ]}
              extraLinks={[
                {
                  title: "VLMcare",
                  href: "/en/services/software-solutions-services/vlmcare",
                  description:
                    lang === "es"
                      ? "Plataforma de validación basada en datos y potenciada por IA para operaciones reguladas. VLMcare unifica la gestión del ciclo de vida de la validación, la orquestación de flujos de trabajo y la preparación para auditorías."
                      : lang === "zh-cn"
                        ? "面向受监管运营的数据驱动、AI 增强验证平台。VLMcare 将验证生命周期管理、工作流编排和审计就绪整合到一个平台。"
                        : "Data-driven, AI-enhanced validation platform for regulated operations. VLMcare unifies validation lifecycle management, workflow orchestration, and audit readiness.",
                  image_url: "/__l5e/assets-v1/f10db7a2-58f6-492a-8678-847cc098f2ce/vlmcare-logo-card.png",
                },
              ]}
              extraLinksPosition="before"
            />

            <ServiceOfferings
              serviceSlug={service.slug}
              color={displayService.color}
              sectionTitle={swHeadings.services}
              excludeIds={[
                "4327024a-3cc4-47d8-a418-7db6eb3aabbf",
                "4125e687-14ad-48ab-8aa7-6e793b7fa6c7",
              ]}
            />
          </>
        ) : (
          <ServiceOfferings
            serviceSlug={service.slug}
            color={displayService.color}
            sectionTitle={q("offerings")}
            eyebrow={toxUnified ? TOXICOLOGY_EYEBROWS.offerings : undefined}
            eyebrowAccent={TOX_ACCENT}
            onlyIds={service.slug === "quality-assurance" && lang === "en" ? [] : undefined}
            extraLinks={
              service.slug === "quality-assurance" && lang === "en"
                ? QA_OVERVIEW_CARDS
                : undefined
            }
            hideFilters={service.slug === "pharmacovigilance"}
            bottomCta={service.slug === "pharmacovigilance" ? { label: ui("getInTouch"), href: "#contact" } : undefined}
          />
        )}
        <ServiceWhyQbd service={displayService} onCtaClick={() => setFormOpen(true)} sectionTitle={q("whyQbd")} eyebrow={toxUnified ? TOXICOLOGY_EYEBROWS.whyQbd : undefined} eyebrowAccent={TOX_ACCENT} />
        
        {service.slug !== "clinical-solutions" && service.slug !== "regulatory-affairs" && <ServiceRelatedContent service={service} eyebrow={toxUnified ? TOXICOLOGY_EYEBROWS.related : undefined} eyebrowAccent={TOX_ACCENT} />}

        {/* HubSpot Form Dialog */}
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">{displayService.formHeading}</DialogTitle>
            <WebinarRegistrationForm
              registrationUrl={null}
              title={displayService.title}
              hubspotFormId={service.hubspotFormId}
              heading={displayService.formHeading}
              subtitle={displayService.formSubtitle}
            />
          </DialogContent>
        </Dialog>

        {service.slug === "clinical-solutions" && <CirAccreditationBlock />}

        {service.slug !== "clinical-solutions" && service.slug !== "regulatory-affairs" && (
          <ContactCTA
            onContactClick={() => setFormOpen(true)}
            title={displayService.formHeading}
            description={displayService.formSubtitle}
          />
        )}
      </main>

      <Footer />
    </>
  );
};

export default ServicePage;
